import React from "react";
import { FaFilter, FaSearch, FaSyncAlt, FaTimes } from "react-icons/fa";

export default function GroupsFiltersCard({
  filters,
  setFilter,
  resetFilters,
  dojos,
  loading,
  filteredCount,
  totalCount,
  isQuotaActive,
  quotaUpdating,
  onToggleQuota,
  onRefresh,
}) {
  const hasFilters =
    filters.query.trim() ||
    filters.dojoId ||
    filters.status !== "all" ||
    filters.quotaState !== "all";

  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="p-4 sm:p-5 lg:p-6">
        <div className="mb-4 flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              Filtres & quota
            </p>

            <h3 className="mt-1 flex items-center gap-2 text-xl font-bold text-slate-900 dark:text-white">
              <FaFilter className="text-indigo-600 dark:text-indigo-300" />
              Recherche des groupes
            </h3>

            <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
              Filtrez par dojo, statut ou remplissage. Le blocage quota
              s’applique au moment des inscriptions.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            {hasFilters ? (
              <button
                type="button"
                onClick={resetFilters}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 shadow-sm transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                <FaTimes />
                Réinitialiser
              </button>
            ) : null}

            <button
              type="button"
              onClick={onRefresh}
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              <FaSyncAlt className={loading ? "animate-spin" : ""} />
              Actualiser
            </button>
          </div>
        </div>

        <div className="grid gap-3 lg:grid-cols-12">
          <div className="relative lg:col-span-4">
            <input
              type="text"
              value={filters.query}
              onChange={(event) => setFilter("query", event.target.value)}
              placeholder="Rechercher nom, discipline, enseignant..."
              className="w-full rounded-2xl border border-slate-200 bg-white py-2.5 pl-10 pr-3 text-sm font-medium text-slate-700 outline-none transition placeholder:text-slate-400 focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
            />

            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          </div>

          <select
            value={filters.dojoId}
            onChange={(event) => setFilter("dojoId", event.target.value)}
            className="rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 lg:col-span-3"
          >
            <option value="">Tous les dojos</option>
            {dojos.map((dojo) => (
              <option key={dojo.id} value={dojo.id}>
                {dojo.dojo_nom}
              </option>
            ))}
          </select>

          <select
            value={filters.status}
            onChange={(event) => setFilter("status", event.target.value)}
            className="rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 lg:col-span-2"
          >
            <option value="all">Tous statuts</option>
            <option value="active">Actifs</option>
            <option value="inactive">Inactifs</option>
          </select>

          <select
            value={filters.quotaState}
            onChange={(event) => setFilter("quotaState", event.target.value)}
            className="rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 lg:col-span-3"
          >
            <option value="all">Tous quotas</option>
            <option value="withQuota">Avec quota</option>
            <option value="withoutQuota">Sans quota</option>
            <option value="available">Places disponibles</option>
            <option value="full">Complets</option>
          </select>
        </div>

        <div className="mt-4 grid gap-3 lg:grid-cols-[1fr_auto] lg:items-center">
          <div className="flex flex-wrap gap-2 text-xs text-slate-600 dark:text-slate-300">
            <span className="rounded-full bg-slate-100 px-3 py-1 font-bold dark:bg-slate-800">
              {filteredCount} / {totalCount} groupe(s)
            </span>

            {hasFilters ? (
              <span className="rounded-full bg-indigo-50 px-3 py-1 font-bold text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
                Filtres actifs
              </span>
            ) : (
              <span className="rounded-full bg-slate-100 px-3 py-1 font-bold dark:bg-slate-800">
                Aucun filtre
              </span>
            )}
          </div>

          <div className="flex flex-col gap-2 rounded-2xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-800 dark:bg-slate-950/50 sm:flex-row sm:items-center sm:justify-between lg:min-w-[430px]">
            <div>
              <p className="text-sm font-black text-slate-900 dark:text-white">
                Blocage à quota
              </p>
              <p className="mt-0.5 text-xs leading-5 text-slate-600 dark:text-slate-300">
                {isQuotaActive
                  ? "Activé : l’inscription est bloquée si le groupe est plein."
                  : "Désactivé : l’inscription reste possible au-delà du quota."}
              </p>
            </div>

            <button
              type="button"
              onClick={onToggleQuota}
              disabled={quotaUpdating}
              className={`inline-flex items-center justify-center rounded-2xl px-4 py-2 text-sm font-bold text-white transition disabled:cursor-not-allowed disabled:opacity-60 ${
                isQuotaActive
                  ? "bg-rose-600 hover:bg-rose-700"
                  : "bg-[#013E8E] hover:bg-[#012f6d] dark:bg-indigo-500 dark:hover:bg-indigo-400"
              }`}
            >
              {quotaUpdating
                ? "Modification..."
                : isQuotaActive
                ? "Désactiver"
                : "Activer"}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}